import { getCountries, getExampleNumber, CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

/**
 * A service for working with phone numbers and country codes.
 */
class PhoneNumberService {
  /**
   * Generate a national phone number format example for a given country.
   * @param country - The ISO 3166-1 alpha-2 country code.
   * @returns The formatted national phone number example.
   */
  private generateNationalPhoneNumber(country: CountryCode): string | undefined {
    const example = getExampleNumber(country, examples);
    return example?.formatNational();
  }

  /**
   * Create a phone number mask by replacing all digits with '9' in the national phone number format example.
   * @example createPhoneMask('BR') -> '(99) 99999-9999'
   * @param country - The ISO 3166-1 alpha-2 country code.
   * @returns The phone number mask with digits replaced by '9'.
   */
  createPhoneMask(country: CountryCode): string | undefined {
    const example = this.generateNationalPhoneNumber(country);
    if (!example) return undefined;

    const mask = example.replace(/\d/g, '9');
    return mask;
  }

  /**
   * Get a list of all country codes. Country codes are the `letters`.
   * @example getAllCountryCodes() -> ['BR', 'US', 'CA', ...]
   * @returns An array of ISO 3166-1 alpha-2 country codes.
   */
  getAllCountryCodes(): string[] {
    return getCountries();
  }

  /**
   * Get the country calling code for a given country. Country calling codes are the `numbers`.
   * @param country - The ISO 3166-1 alpha-2 country code.
   * @returns {string | undefined} The country calling code as a string, or undefined if an error occurs.
   * @example getCountryCallingCode('GB') -> '44'
   */
  getCountryCallingCode(country: CountryCode): string | undefined {
    try {
      return getCountryCallingCode(country);
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }
}

export { PhoneNumberService };
